import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CheckIcon } from '@heroicons/react/24/solid';
import { useProjects } from '../../hooks/useProjects';
import { useTemplates, useCreateDispatch } from '../../hooks/useSurveys';
import { contactService } from '../../services/contact.service';
import { useQuery } from '@tanstack/react-query';
import type { Contact } from '../../types';

const dispatchSchema = z.object({
  project_id: z.string().min(1, 'Please select a project'),
  template_id: z.string().min(1, 'Please select a template'),
  contact_ids: z.array(z.string()).min(1, 'Please select at least one contact'),
  scheduled_at: z.string().optional(),
  expires_at: z.string().optional(),
  reminder_days: z.array(z.number()).optional(),
});

type DispatchFormData = z.infer<typeof dispatchSchema>;

const STEPS = [
  { id: 1, title: 'Select Project' },
  { id: 2, title: 'Select Template' },
  { id: 3, title: 'Select Contacts' },
  { id: 4, title: 'Review & Confirm' },
];

export default function DispatchWizard() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);

  const { data: projectsData, isLoading: projectsLoading } = useProjects({ pageSize: 100 });
  const { data: templates, isLoading: templatesLoading } = useTemplates();
  const createDispatch = useCreateDispatch();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<DispatchFormData>({
    resolver: zodResolver(dispatchSchema),
    defaultValues: {
      project_id: '',
      template_id: '',
      contact_ids: [],
      scheduled_at: '',
      expires_at: '',
      reminder_days: [],
    },
  });

  const selectedProjectId = watch('project_id');
  const selectedContactIds = watch('contact_ids');

  // Fetch contacts for the selected project's client
  const selectedProject = projectsData?.data?.find((p) => p.id === selectedProjectId);
  const clientId = selectedProject?.client_id || '';

  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['contacts', 'client', clientId],
    queryFn: () => contactService.getByClient(clientId),
    enabled: !!clientId,
  });

  const activeTemplates = (templates ?? []).filter((t) => t.status === 'active');

  const nextStep = async () => {
    let valid = true;
    if (currentStep === 1) {
      valid = await trigger('project_id');
    } else if (currentStep === 2) {
      valid = await trigger('template_id');
    } else if (currentStep === 3) {
      const ids = watch('contact_ids');
      if (!ids || ids.length === 0) {
        valid = false;
        alert('Please select at least one contact');
        return;
      }
    }

    if (valid && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleContact = (contactId: string) => {
    const current = selectedContactIds || [];
    if (current.includes(contactId)) {
      setValue(
        'contact_ids',
        current.filter((id) => id !== contactId),
      );
    } else {
      setValue('contact_ids', [...current, contactId]);
    }
  };

  const selectAllContacts = (contactsList: Contact[]) => {
    setValue(
      'contact_ids',
      contactsList.map((c) => c.id),
    );
  };

  const onSubmit = async (data: DispatchFormData) => {
    try {
      const payload = {
        template_id: data.template_id,
        project_id: data.project_id,
        contact_ids: data.contact_ids,
        scheduled_at: data.scheduled_at || undefined,
        expires_at: data.expires_at || undefined,
        reminder_days: data.reminder_days?.length ? data.reminder_days : undefined,
      };
      await createDispatch.mutateAsync(payload);
      navigate('/surveys/dispatches');
    } catch {
      // Error handled by React Query
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Create Survey Dispatch</h1>

      {/* Step Indicator */}
      <nav aria-label="Progress">
        <ol className="flex items-center">
          {STEPS.map((step, idx) => (
            <li key={step.id} className={`flex-1 ${idx < STEPS.length - 1 ? 'pr-8' : ''}`}>
              <div className="flex items-center">
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                    step.id < currentStep
                      ? 'bg-indigo-600 text-white'
                      : step.id === currentStep
                        ? 'border-2 border-indigo-600 text-indigo-600'
                        : 'border-2 border-gray-300 text-gray-500'
                  }`}
                >
                  {step.id < currentStep ? (
                    <CheckIcon className="h-4 w-4" />
                  ) : (
                    step.id
                  )}
                </div>
                <span
                  className={`ml-2 text-sm font-medium ${
                    step.id <= currentStep ? 'text-indigo-600' : 'text-gray-500'
                  }`}
                >
                  {step.title}
                </span>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`ml-4 flex-1 h-0.5 ${
                      step.id < currentStep ? 'bg-indigo-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            </li>
          ))}
        </ol>
      </nav>

      <div>
        {/* Step 1: Select Project */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Select Project</h2>
            <p className="text-sm text-gray-500">
              Choose the project this survey dispatch is associated with.
            </p>

            {projectsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {projectsData?.data?.map((project) => (
                  <label
                    key={project.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProjectId === project.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      value={project.id}
                      {...register('project_id')}
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{project.name}</p>
                      <p className="text-xs text-gray-500">
                        {project.client_name} · {project.status}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            {errors.project_id && (
              <p className="text-sm text-red-600">{errors.project_id.message}</p>
            )}
          </div>
        )}

        {/* Step 2: Select Template */}
        {currentStep === 2 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Select Template</h2>
            <p className="text-sm text-gray-500">
              Choose an active survey template to use.
            </p>

            {templatesLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
              </div>
            ) : activeTemplates.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {activeTemplates.map((template) => (
                  <label
                    key={template.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      watch('template_id') === template.id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      value={template.id}
                      {...register('template_id')}
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{template.name}</p>
                      <p className="text-xs text-gray-500">
                        {template.questions?.length ?? 0} questions · v{template.version ?? 1}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-4">
                No active templates available.{' '}
                <a href="/surveys/templates/new" className="text-indigo-600 hover:underline">
                  Create one first.
                </a>
              </p>
            )}
            {errors.template_id && (
              <p className="text-sm text-red-600">{errors.template_id.message}</p>
            )}
          </div>
        )}

        {/* Step 3: Select Contacts */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Select Contacts</h2>
                <p className="text-sm text-gray-500">
                  Choose which contacts will receive the survey invitation.
                </p>
              </div>
              {contacts && contacts.length > 0 && (
                <button
                  type="button"
                  onClick={() => selectAllContacts(contacts)}
                  className="text-sm text-indigo-600 hover:text-indigo-500"
                >
                  Select All
                </button>
              )}
            </div>

            {contactsLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
              </div>
            ) : contacts && contacts.length > 0 ? (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {(contacts ?? [])
                  .filter((c) => c.status !== 'inactive' && !c.opt_out)
                  .map((contact) => (
                    <label
                      key={contact.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedContactIds?.includes(contact.id)
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContactIds?.includes(contact.id) || false}
                        onChange={() => toggleContact(contact.id)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {contact.first_name} {contact.last_name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {contact.email}
                          {contact.position && ` · ${contact.position}`}
                        </p>
                      </div>
                    </label>
                  ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 py-4">
                No contacts found for this project&apos;s client.
              </p>
            )}

            {selectedContactIds && selectedContactIds.length > 0 && (
              <p className="text-sm text-gray-600">
                {selectedContactIds.length} contact{selectedContactIds.length > 1 ? 's' : ''} selected
              </p>
            )}
            {errors.contact_ids && (
              <p className="text-sm text-red-600">{errors.contact_ids.message}</p>
            )}
          </div>
        )}

        {/* Step 4: Review & Confirm */}
        {currentStep === 4 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Review & Confirm</h2>
            <p className="text-sm text-gray-500">
              Please review the dispatch details before creating.
            </p>

            {/* Summary */}
            <div className="rounded-lg bg-gray-50 p-4 border border-gray-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Project</h3>
                <p className="mt-1 text-sm text-gray-700">{selectedProject?.name || '—'}</p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900">Template</h3>
                <p className="mt-1 text-sm text-gray-700">
                  {activeTemplates.find((t) => t.id === watch('template_id'))?.name || '—'}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Recipients ({selectedContactIds?.length || 0} contact{(selectedContactIds?.length || 0) !== 1 ? 's' : ''})
                </h3>
                <ul className="mt-1 space-y-1 max-h-48 overflow-y-auto">
                  {(contacts ?? [])
                    .filter((c) => selectedContactIds?.includes(c.id))
                    .map((contact) => (
                      <li key={contact.id} className="text-sm text-gray-700">
                        {contact.first_name} {contact.last_name} — <span className="text-gray-500">{contact.email}</span>
                      </li>
                    ))}
                </ul>
              </div>
            </div>

            {/* Scheduling options */}
            <div className="space-y-4">
              <div>
                <label htmlFor="scheduled_at" className="block text-sm font-medium text-gray-700">
                  Schedule For (optional)
                </label>
                <input
                  id="scheduled_at"
                  type="datetime-local"
                  {...register('scheduled_at')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave empty to create as draft (send later).
                </p>
              </div>

              <div>
                <label htmlFor="expires_at" className="block text-sm font-medium text-gray-700">
                  Expiry Date (optional)
                </label>
                <input
                  id="expires_at"
                  type="datetime-local"
                  {...register('expires_at')}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Reminder Days (optional)
                </label>
                <div className="mt-2 flex items-center gap-3">
                  {[3, 5, 7, 14].map((day) => (
                    <label key={day} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        value={day}
                        onChange={(e) => {
                          const current = watch('reminder_days') || [];
                          if (e.target.checked) {
                            setValue('reminder_days', [...current, day]);
                          } else {
                            setValue(
                              'reminder_days',
                              current.filter((d) => d !== day),
                            );
                          }
                        }}
                        checked={(watch('reminder_days') || []).includes(day)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700">{day} days</span>
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Send reminders after these many days if not completed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6">
          <button
            type="button"
            onClick={currentStep === 1 ? () => navigate('/surveys/dispatches') : prevStep}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            {currentStep === 1 ? 'Cancel' : 'Back'}
          </button>

          {currentStep < 4 ? (
            <button
              type="button"
              onClick={nextStep}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={createDispatch.isPending}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
            >
              {createDispatch.isPending
                ? 'Creating...'
                : 'Create Dispatch'}
            </button>
          )}
        </div>

        {createDispatch.isError && (
          <p className="mt-4 text-sm text-red-600">
            Failed to create dispatch. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}
